import { useState } from 'react';

import { Card, IconButton } from '@opositandopn/ui';
import classnames from 'classnames';
import { IoArrowForwardCircleOutline } from 'react-icons/io5';

import MainImg from '../../../assets/images/30ae0bdb-b475-4c5b-9590-d2f5493b9401.webp';
import { ResourceDialog } from '../../../components/shared/resource-dialog/ResourceDialog';
import { THEORY_SUBJECT_ID } from '../../../constants/subjects';
import { useIsPremium, useProfile } from '../../../context/ProfileContext';
import { useAppActions } from '../../../state/appStore';
import { BaseComponentProps } from '../../../types/base-component.types';
import { AssessmentPreviewModel } from '../../assessments/api/useMultipleChoiceAssessments';
import { AssessmentMark } from '../../assessments/components/assessment-mark/AssessmentMark';
import { AssessmentScoreboard } from '../../assessments/components/assessment-scoreboard/AssessmentScoreboard';
import { AssessmentSummary } from '../../assessments/components/assessment-summary/AssessmentSummary';
import { MultipleChoiceAssessmentAttemptDialog } from '../../assessments/components/multiple-choice-assessment-attempt-dialog/MultipleChoiceAssessmentAttemptDialog';
import { MultipleChoiceAssessmentPreview } from '../../assessments/components/multiple-choice-assessment-preview/MultipleChoiceAssessmentPreview';
import { useLatestAssessment } from '../../assessments/useLatestAssessment';

import styles from './CompetitiveQuestionnaireCard.module.scss';

function CompetitiveQuestionnaireCard({
  className,
  testId = 'ui-competitive-questionnaire-card',
}: BaseComponentProps) {
  const isPremium = useIsPremium();
  const { profile } = useProfile();
  const { setShowGetPremiumPopup } = useAppActions();

  const [previewAssessmentId, setPreviewAssessmentId] = useState<string>();
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>();

  const {
    assessment: currentAssessment,
    metrics: metricsResponse,
    ownGrade,
    isLoading,
    isMetricsLoading,
  } = useLatestAssessment(THEORY_SUBJECT_ID, {
    perspectives: ['instructor'],
    statuses: ['published'],
    purposes: ['summative'],
    referenceStandards: ['norm-referenced'],
    methodologies: ['quantitative'],
    settings: ['standardized'],
    official: false,
    released: true,
  });

  const handleStart = (assessment: AssessmentPreviewModel<string>) => {
    // Private simulacros are premium content — same rule as the simulacro list.
    if (assessment.visibility === 'private' && !isPremium) {
      setShowGetPremiumPopup(true);
      return;
    }

    // The button opens the simulacro's preview, as the list and the weekly cards
    // do; sitting it is the arrow inside that preview.
    setPreviewAssessmentId(assessment.id);
  };

  return (
    <Card
      className={classnames(styles['card'], className)}
      loading={isLoading || isMetricsLoading}
      testId={testId}
    >
      {currentAssessment && (
        <div className={styles['main']}>
          {/* A column of its own on a wide screen, standing on the card's foot;
              below lg it leaves the flow for the card's bottom-left corner,
              under everything else; below 480px it stands centred on that foot,
              with the card's content clearing it. */}
          <div className={styles['illustration']} aria-hidden="true">
            <img className={styles['image']} src={MainImg} alt="" />
          </div>
          <div className={styles['summary']}>
            <AssessmentSummary
              testId={`${testId}_summary`}
              heading="Simulacro mensual de teoría"
              name={currentAssessment.name}
              timeLimit={currentAssessment.timeLimit}
              questionCount={currentAssessment.questions.length}
            />
            <AssessmentMark
              testId={`${testId}_mark`}
              score={ownGrade}
              shadow={true}
              onStart={() => handleStart(currentAssessment)}
            />
          </div>
          {/* Always on the card, empty until the first mark: the board is where
              the simulacro is read from, and a card that grows one the moment
              somebody finishes reads as a different card each month. */}
          {metricsResponse && (
            <AssessmentScoreboard
              className={styles['scoreboard']}
              testId={`${testId}_scoreboard`}
              metrics={metricsResponse}
              ownUsername={profile.username}
              cutoffScore={currentAssessment.cutoffScore}
            />
          )}
        </div>
      )}
      <ResourceDialog open={previewAssessmentId !== undefined}>
        {previewAssessmentId && (
          <MultipleChoiceAssessmentPreview
            subjectId={THEORY_SUBJECT_ID}
            assessmentId={previewAssessmentId}
            onClose={() => setPreviewAssessmentId(undefined)}
          >
            {/* The preview is closed on the way into the simulacro, so that closing
                the simulacro lands back on the card rather than on the preview it
                was started from. */}
            <IconButton
              icon={<IoArrowForwardCircleOutline />}
              onClick={() => {
                setSelectedAssessmentId(previewAssessmentId);
                setPreviewAssessmentId(undefined);
              }}
            />
          </MultipleChoiceAssessmentPreview>
        )}
      </ResourceDialog>
      <MultipleChoiceAssessmentAttemptDialog
        subjectId={THEORY_SUBJECT_ID}
        assessmentId={selectedAssessmentId}
        onClose={() => setSelectedAssessmentId(undefined)}
      />
    </Card>
  );
}

export { CompetitiveQuestionnaireCard };
